import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { SYSTEM_USER_ID } from "../../../utils/constants";
import { useGetAllSuppliers, useGetGrazeons } from "../queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";
import {
  formatDiffFromPrevious,
  LifecycleStep,
  toMillis,
} from "../../ControlTower/buildLifecycleSteps";
import { formatLifecycleCell } from "../../ControlTower/formatLifecycleCell";
import LifecycleStepsTable from "../../ControlTower/LifecycleStepsTable";
import {
  AuditTrailStage,
  buildAuditTrailStages,
  formatTimelineStatus,
  OrderTimelineEntry,
} from "./auditTrailSteps";

export type { OrderTimelineEntry };

type AuditTrailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderTimeline?: OrderTimelineEntry[] | null;
  createdAt?: string | Date | null;
};

const idKey = (id?: string | null) => (id ? String(id) : "");

const AuditTrailModal = ({
  isOpen,
  onClose,
  orderTimeline,
  createdAt,
}: AuditTrailModalProps) => {
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: grazeons } = useGetGrazeons();
  const orderHandlers = useOrdersStore((state) => state.orderHandlers);

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};

    orderHandlers?.forEach((handler: any) => {
      const user = handler?.user;
      const key = idKey(user?._id || handler?.userId || handler?._id);
      if (!key) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    const grazeonList = Array.isArray(grazeons?.data)
      ? grazeons.data
      : Array.isArray(grazeons)
        ? grazeons
        : [];
    grazeonList.forEach((grazeon: any) => {
      const user = grazeon?.user;
      const key = idKey(user?._id || grazeon?.userId);
      if (!key || map[key]) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    return map;
  }, [orderHandlers, grazeons]);

  const supplierNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (allSuppliers || []).forEach((supplier: any) => {
      const key = idKey(supplier?._id);
      if (key && supplier?.businessName) {
        map[key] = supplier.businessName;
      }
    });
    return map;
  }, [allSuppliers]);

  const resolvePerformedBy = (stage: AuditTrailStage) => {
    const userId = idKey(stage.userId);
    const supplierName = supplierNameById[idKey(stage.supplierId)];
    const userName =
      userId === SYSTEM_USER_ID
        ? "SYSTEM"
        : userNameById[userId];

    if (userName && supplierName) {
      return `${userName} · ${supplierName}`;
    }
    if (userName) return userName;
    if (supplierName) return supplierName;
    if (stage.userId || stage.supplierId) return "Unknown";
    return "—";
  };

  const steps: LifecycleStep[] = useMemo(() => {
    if (!Array.isArray(orderTimeline)) return [];

    const sorted = [...orderTimeline].sort((a, b) => {
      const aTime = a?.at ? new Date(a.at).getTime() : 0;
      const bTime = b?.at ? new Date(b.at).getTime() : 0;
      return aTime - bTime;
    });

    let previousMs = 0;
    return buildAuditTrailStages(sorted, createdAt).map((stage, idx) => {
      const currentMs = toMillis(stage.at ?? null);
      const atRaw = (() => {
        if (stage.at instanceof Date) {
          const ms = stage.at.getTime();
          return Number.isFinite(ms) ? stage.at.toISOString() : null;
        }
        if (stage.at != null) return String(stage.at);
        return null;
      })();
      const diffFromPrevious =
        stage.diffFromPrevious ??
        (previousMs <= 0 || currentMs <= 0
          ? "0"
          : formatDiffFromPrevious(previousMs, currentMs));
      if (currentMs > 0) previousMs = currentMs;

      return {
        id: `${stage.scope}-${stage.status}-${atRaw}-${idx}`,
        status: formatTimelineStatus(stage.status) ?? "—",
        at: formatLifecycleCell(stage.at ?? atRaw),
        atRaw,
        person: resolvePerformedBy(stage),
        diffFromPrevious,
      };
    });
    // resolvePerformedBy depends on name maps; include those deps explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTimeline, createdAt, userNameById, supplierNameById]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader py={3} fontSize="md">
          Audit Trail
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={2} overflowY="auto">
          <LifecycleStepsTable
            steps={steps}
            emptyMessage="No audit trail events available."
          />
        </ModalBody>
        <ModalFooter py={2}>
          <Button colorScheme="blue" size="sm" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuditTrailModal;
